import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { ArrowRight, Layers3 } from 'lucide-react';

import { Expansion, ProductType, Universe } from '../../types';
import { cn } from '../../utils';

type Props = {
    universe: Universe;
    expansions: Expansion[];
    leftOffset?: number;
    open: boolean;
    onNavigate: () => void;
    currentExpansionKey?: string;
};

type CategoriesColumnProps = {
    universe: Universe;
    onNavigate: () => void;
};

const CategoriesColumn: FC<CategoriesColumnProps> = ({
    universe,
    onNavigate,
}) => (
    <div className="p-4">
        <p className="mb-1.5 text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            Категорії
        </p>
        <div className="space-y-0.5">
            <Link
                href={universe.url_path}
                onClick={onNavigate}
                className="block rounded-lg px-1.5 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-primary"
            >
                Усі товари
            </Link>
            {universe.product_types.map((productType) => (
                <Link
                    key={productType.id}
                    href={productType.url_path}
                    onClick={onNavigate}
                    className="block rounded-lg px-1.5 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-primary"
                >
                    {productType.name}
                </Link>
            ))}
        </div>
    </div>
);

type ExpansionsColumnProps = {
    expansions: Expansion[];
    primaryProductType?: ProductType;
    universe: Universe;
    onNavigate: () => void;
    currentExpansionKey?: string;
};

const ExpansionsColumn: FC<ExpansionsColumnProps> = ({
    expansions,
    primaryProductType,
    universe,
    onNavigate,
    currentExpansionKey,
}) => {
    const expansionBasePath = primaryProductType?.url_path ?? universe.url_path;

    return (
        <div className="border-l p-4">
            <div className="mb-1.5 flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                    <Layers3 className="h-4 w-4" />
                    Сети
                </p>
                <Link
                    href={expansionBasePath}
                    onClick={onNavigate}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                    Усі сети
                    <ArrowRight className="h-4 w-4" />
                </Link>
            </div>

            {expansions.length > 0 ? (
                <div className="space-y-0.5">
                    {expansions.map((expansion) => {
                        const expansionKey = expansion.tag || expansion.slug;
                        const isCurrent = expansionKey === currentExpansionKey;

                        return (
                            <Link
                                key={expansion.id}
                                href={`${expansionBasePath}/${expansion.slug}`}
                                onClick={onNavigate}
                                aria-current={isCurrent ? 'page' : undefined}
                                className={cn(
                                    'group/set flex items-center justify-between gap-3 rounded-lg px-1.5 py-1.5 text-sm transition-colors hover:bg-accent hover:text-primary',
                                    isCurrent && 'bg-accent text-primary',
                                )}
                            >
                                <span className="min-w-0 truncate font-medium">
                                    {expansion.name}
                                </span>
                                <span
                                    className={cn(
                                        'shrink-0 text-sm font-medium text-muted-foreground transition-colors group-hover/set:text-primary',
                                        isCurrent && 'text-primary',
                                    )}
                                >
                                    {expansion.tag}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            ) : (
                <p className="rounded-xl border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                    Сети для цієї гри скоро з’являться.
                </p>
            )}
        </div>
    );
};

export const HeaderUniverseMenu: FC<Props> = ({
    universe,
    expansions,
    leftOffset,
    open,
    onNavigate,
    currentExpansionKey,
}) => {
    const primaryProductType =
        universe.product_types.find(
            (productType) => productType.slug === 'singles',
        ) ?? universe.product_types[0];

    return (
        <div
            id={`header-universe-menu-${universe.id}`}
            data-testid={`header-universe-menu-${universe.slug}`}
            style={{ left: leftOffset ?? 0 }}
            aria-hidden={!open}
            className={cn(
                'absolute top-full z-50 w-[36rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-b-2xl border bg-popover shadow-2xl transition-opacity duration-150',
                open ? 'visible opacity-100' : 'invisible opacity-0',
            )}
        >
            <div className="grid grid-cols-[11.5rem_minmax(0,1fr)]">
                <CategoriesColumn universe={universe} onNavigate={onNavigate} />
                <ExpansionsColumn
                    universe={universe}
                    expansions={expansions}
                    primaryProductType={primaryProductType}
                    onNavigate={onNavigate}
                    currentExpansionKey={currentExpansionKey}
                />
            </div>
        </div>
    );
};
